import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, Clock, User, Scissors, MapPin, Navigation, Camera, Upload, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatTime12Hour, formatDateDisplay } from '../utils/timeFormat';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const MyAppointments = () => {
  const { currentUser } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingNotification, setSendingNotification] = useState(null);
  const [cancellingAppointment, setCancellingAppointment] = useState(null);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);

  useEffect(() => {
    const fetchAppointments = async () => {
      if (currentUser) {
        try {
          const response = await api.get('/api/appointments/me');
          setAppointments(response.data);
        } catch (error) {
          console.error('Error fetching appointments:', error);
          toast.error('Failed to load appointments');
        } finally {
          setLoading(false);
        }
      }
    };
    fetchAppointments();
  }, [currentUser]);

  // Helper function to create a Date object from date and time strings
  const createAppointmentDateTime = (apt) => {
    if (apt.start_time) return new Date(apt.start_time); // Legacy support
    if (apt.date && apt.time) {
      return new Date(`${apt.date}T${apt.time}:00`);
    }
    return new Date(); // Fallback
  };

  const now = new Date();
  const upcomingAppointments = appointments.filter(apt => createAppointmentDateTime(apt) >= now);
  const pastAppointments = appointments.filter(apt => createAppointmentDateTime(apt) < now);

  const handleOnMyWay = async (appointment) => {
    setSendingNotification(appointment.id);
    
    try {
      const response = await api.post('/api/on-my-way/notify', {
        appointment_id: appointment.id,
        eta_minutes: 15  // Default 15 minute ETA
      });
      
      // Update local state
      setAppointments(appointments.map(apt => 
        apt.id === appointment.id 
          ? { ...apt, customer_en_route: true, en_route_notified_at: response.data.notified_at }
          : apt
      ));
      
      toast.success(`🚗 ${response.data.barber_name} has been notified!`, {
        description: "Your barber knows you're on the way",
        duration: 4000
      });
    } catch (error) {
      console.error('Error sending on my way notification:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to notify barber';
      toast.error(errorMsg);
    } finally {
      setSendingNotification(null);
    }
  };

  const handleUploadSelfie = () => {
    toast.info('Photo upload coming soon!', {
      description: 'Share your fresh cut and enter monthly contests'
    });
  };

  const handleCancelAppointment = async () => {
    if (!appointmentToCancel) return;
    
    setCancellingAppointment(appointmentToCancel.id);
    
    try {
      await api.delete(`/api/appointments/${appointmentToCancel.id}`);
      
      // Remove from local state
      setAppointments(appointments.filter(apt => apt.id !== appointmentToCancel.id));
      
      toast.success('Appointment cancelled successfully', {
        description: 'Your appointment has been removed from the schedule',
        duration: 4000
      });
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to cancel appointment';
      toast.error(errorMsg);
    } finally {
      setCancellingAppointment(null);
      setAppointmentToCancel(null);
    }
  };
  
  const isWithinTwoHours = (appointment) => {
    const now = new Date();
    const appointmentDate = createAppointmentDateTime(appointment);
    const twoHoursFromNow = new Date(now.getTime() + (2 * 60 * 60 * 1000));
    return appointmentDate <= twoHoursFromNow;
  };

  const AppointmentCard = ({ appointment, isPast = false }) => {
    const appointmentDate = createAppointmentDateTime(appointment);
    const isToday = appointmentDate.toDateString() === new Date().toDateString();
    const canNotifyOnMyWay = isToday && !appointment.customer_en_route && !isPast;
    const alreadyNotified = appointment.customer_en_route;
    const withinTwoHours = isWithinTwoHours(appointment);
    const canCancel = !isPast && !withinTwoHours && appointment.status !== 'cancelled';

    return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="space-y-3 flex-1">
            <div>
              <h3 className="text-xl font-semibold text-white mb-1">{appointment.service_name}</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                  appointment.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                  appointment.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {appointment.status.toUpperCase()}
                </span>
                
                {alreadyNotified && (
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-lime-500/20 text-lime-400 flex items-center">
                    <Navigation className="w-3 h-3 mr-1" />
                    ON THE WAY - ETA 15 MIN
                  </span>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center text-zinc-300">
                <Calendar className="w-4 h-4 mr-2 text-lime-400" />
                {appointment.date
                  ? formatDateDisplay(appointment.date)
                  : appointmentDate.toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
              </div>
              <div className="flex items-center text-zinc-300">
                <Clock className="w-4 h-4 mr-2 text-lime-400" />
                {appointment.time
                  ? formatTime12Hour(appointment.time)
                  : appointmentDate.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
              </div>
              <div className="flex items-center text-zinc-300">
                <Scissors className="w-4 h-4 mr-2 text-lime-400" />
                {appointment.barber_name}
              </div>
              <div className="flex items-center text-zinc-300">
                <MapPin className="w-4 h-4 mr-2 text-lime-400" />
                Sauce Sity Barbershop
              </div>
            </div>

            {appointment.notes && (
              <div className="text-sm text-zinc-400 bg-zinc-800 p-3 rounded">
                <strong>Notes:</strong> {appointment.notes}
              </div>
            )}
          </div>

          <div className="mt-4 md:mt-0 md:ml-6 flex flex-col space-y-2 min-w-[200px]">
            {canNotifyOnMyWay && (
              <Button 
                onClick={() => handleOnMyWay(appointment)}
                disabled={sendingNotification === appointment.id}
                className="w-full bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700"
              >
                <Navigation className="w-4 h-4 mr-2" />
                {sendingNotification === appointment.id ? 'Notifying...' : "I'm On My Way"}
              </Button>
            )}
            
            {!isPast && (
              <Button 
                onClick={() => setAppointmentToCancel(appointment)}
                disabled={!canCancel || cancellingAppointment === appointment.id}
                variant="outline"
                className={`w-full ${
                  canCancel 
                    ? 'border-red-400 text-red-400 hover:bg-red-400 hover:text-black' 
                    : 'border-zinc-600 text-zinc-600 cursor-not-allowed'
                }`}
                title={!canCancel && withinTwoHours ? 'Cannot cancel within 2 hours of appointment' : ''}
              >
                <XCircle className="w-4 h-4 mr-2" />
                {cancellingAppointment === appointment.id ? 'Cancelling...' : 'Cancel Appointment'}
              </Button>
            )}
            
            {!canCancel && withinTwoHours && !isPast && (
              <p className="text-xs text-zinc-500 text-center">
                ⏰ Too late to cancel (within 2hrs)
              </p>
            )}
            
            {isPast && appointment.status === 'completed' && (
              <Button 
                onClick={handleUploadSelfie}
                variant="outline"
                className="w-full border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-black"
              >
                <Camera className="w-4 h-4 mr-2" />
                Upload Selfie
              </Button>
            )}
            
            <a href="tel:2392018105">
              <Button variant="outline" className="w-full border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black">
                Call Shop
              </Button>
            </a>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

  return (
    <div className="sauce-sity-page" data-testid="my-appointments-page">
      <div className="sauce-sity-content" style={{ maxWidth: 1100 }}>
        <div className="mb-12">
          <h1 className="ss-title-xl mb-2">
            My <span className="ss-highlight-green">Appointments</span>
          </h1>
          <p className="text-zinc-400">Manage your bookings and appointment history</p>
        </div>

        <Tabs defaultValue="upcoming" className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="upcoming" className="data-[state=active]:bg-lime-400 data-[state=active]:text-black">
              Upcoming ({upcomingAppointments.length})
            </TabsTrigger>
            <TabsTrigger value="past" className="data-[state=active]:bg-lime-400 data-[state=active]:text-black">
              Past ({pastAppointments.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="space-y-4">
            {upcomingAppointments.length > 0 ? (
              upcomingAppointments.map(apt => (
                <AppointmentCard key={apt.id} appointment={apt} />
              ))
            ) : (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-12 text-center">
                  <Calendar className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
                  <h3 className="text-xl font-semibold mb-2">No Upcoming Appointments</h3>
                  <p className="text-zinc-400 mb-6">Ready for a fresh look?</p>
                  <Button className="bg-gradient-to-r from-lime-500 to-lime-600 text-black font-semibold hover:from-lime-600 hover:to-lime-700">
                    Book Appointment
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="past" className="space-y-4">
            {pastAppointments.length > 0 ? (
              pastAppointments.map(apt => (
                <AppointmentCard key={apt.id} appointment={{...apt, status: 'completed'}} isPast={true} />
              ))
            ) : (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-12 text-center">
                  <Clock className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
                  <h3 className="text-xl font-semibold mb-2">No Past Appointments</h3>
                  <p className="text-zinc-400">Your appointment history will appear here</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        
        {/* Cancel Confirmation Dialog */}
        <AlertDialog open={!!appointmentToCancel} onOpenChange={(open) => !open && setAppointmentToCancel(null)}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-400">Cancel Appointment?</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-300">
                Are you sure you want to cancel your appointment for <strong>{appointmentToCancel?.service_name}</strong> on{' '}
                <strong>
                  {appointmentToCancel && createAppointmentDateTime(appointmentToCancel).toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    month: 'short', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </strong>?
                <br /><br />
                This action cannot be undone. Your barber will be notified of the cancellation.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
                Keep Appointment
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleCancelAppointment}
                className="bg-red-500 text-white hover:bg-red-600"
                disabled={cancellingAppointment}
              >
                {cancellingAppointment ? 'Cancelling...' : 'Yes, Cancel'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default MyAppointments;
